import {
  HOME_COLUMNS,
  HOME_MAX_ROWS,
  closestHomeSize,
  type HomeCell,
  type HomePlacement,
  type HomeSize,
} from './homeLayout';

export interface HomeBox {
  left: number;
  top: number;
  width: number;
  height: number;
}
export type HomeRects = Map<string, { node: HTMLElement; box: HomeBox }>;
interface MotionTokens {
  reflow: number;
  stagger: number;
  drop: number;
  mode: number;
  reduced: number;
  hover: number;
  slop: number;
  scale: number;
  appear: number;
  edge: number;
  speed: number;
  easing: string;
  reduce: boolean;
}
const flights = new WeakMap<HTMLElement, Animation>();
const exits = new WeakMap<HTMLElement, Map<string, { node: HTMLElement; animation: Animation }>>();
const box = (rect: DOMRect): HomeBox => ({
  left: rect.left,
  top: rect.top,
  width: rect.width,
  height: rect.height,
});
function number(style: CSSStyleDeclaration, name: string, base = 0) {
  const value = parseFloat(style.getPropertyValue(name));
  return Number.isFinite(value) ? value : base;
}
function time(style: CSSStyleDeclaration, name: string) {
  const value = style.getPropertyValue(name).trim() || style.getPropertyValue('--motion-normal').trim();
  return parseFloat(value) * (value.endsWith('ms') ? 1 : 1000);
}
export function homeMotionTokens(element: HTMLElement, delay: 0 | 400 | 800 | 1500 = 0): MotionTokens {
  const style = getComputedStyle(element);
  return {
    reflow: time(style, '--home-motion-reflow'),
    stagger: time(style, '--home-motion-stagger'),
    drop: time(style, '--home-motion-drop'),
    mode: time(style, '--home-motion-mode'),
    reduced: time(style, '--home-motion-reduced'),
    hover:
      delay === 0
        ? 0
        : time(
            style,
            delay === 400
              ? '--home-hover-short'
              : delay === 1500
                ? '--home-hover-long'
                : '--home-hover-normal',
          ),
    slop: number(style, '--home-drag-slop'),
    scale: number(style, '--home-drag-scale', 1),
    appear: number(style, '--home-appear-scale', 1),
    edge: number(style, '--home-scroll-edge'),
    speed: number(style, '--home-scroll-speed'),
    easing: style.getPropertyValue('--ease-out-expo').trim() || 'ease-out',
    reduce: matchMedia('(prefers-reduced-motion: reduce)').matches,
  };
}
function widgets(canvas: HTMLElement) {
  return [...canvas.querySelectorAll<HTMLElement>('[data-home-widget]')];
}
function motionFrame(element: HTMLElement): HTMLElement {
  const parent = element.parentElement;
  return parent?.hasAttribute('data-home-motion-frame') ? parent : element;
}
export function captureHomeRects(canvas: HTMLElement | null): HomeRects {
  return new Map(
    canvas
      ? widgets(canvas).map((node) => [
          node.dataset.homeWidget!,
          { node, box: box(node.getBoundingClientRect()) },
        ])
      : [],
  );
}
export function animateHomeRects(canvas: HTMLElement | null, before: HomeRects, skipId?: string) {
  if (!canvas) return;
  const config = homeMotionTokens(canvas),
    nodes = widgets(canvas);
  for (const node of nodes) if (node.dataset.homeWidget !== skipId) flights.get(node)?.cancel();
  const positions = nodes
    .filter((node) => node.dataset.homeWidget !== skipId)
    .map((node) => ({
      node,
      old: before.get(node.dataset.homeWidget!)?.box,
      next: box(node.getBoundingClientRect()),
    }));
  const moving = positions.filter(
    ({ old, next }) =>
      old &&
      (Math.abs(old.left - next.left) + Math.abs(old.top - next.top) >= 0.1 ||
        Math.abs((next.width ? old.width / next.width : 1) - 1) +
          Math.abs((next.height ? old.height / next.height : 1) - 1) >=
          0.001),
  );
  const preview = canvas.querySelector<HTMLElement>('[data-home-drop-preview]'),
    currentIds = new Set(nodes.map((node) => node.dataset.homeWidget)),
    removed = [...before].find(([id]) => !currentIds.has(id))?.[1].box,
    active =
      positions.find(({ node }) => node.dataset.homeGrabbed === 'true') ??
      positions.find((item) => !item.old) ??
      moving.find(({ node }) => node.dataset.homeMotion === 'settling'),
    focal = preview
      ? box(preview.getBoundingClientRect())
      : (removed ??
        active?.next ??
        positions.find((item) => !item.old)?.next ??
        moving[0]?.next ??
        box(canvas.getBoundingClientRect())),
    centerX = focal.left + focal.width / 2,
    centerY = focal.top + focal.height / 2;
  const distance = (target: HomeBox) =>
    Math.hypot(target.left + target.width / 2 - centerX, target.top + target.height / 2 - centerY);
  const ordered = moving.sort((a, b) => distance(a.next) - distance(b.next));
  for (const item of positions) {
    const { node, old, next } = item,
      returning = exits.get(canvas)?.get(node.dataset.homeWidget!),
      returnBox = returning ? box(returning.node.getBoundingClientRect()) : null,
      returnOpacity = returning ? getComputedStyle(returning.node).opacity : undefined,
      isNew = !old,
      landing = node.dataset.homeMotion === 'settling';
    if (returning) {
      returning.animation.cancel();
      returning.node.remove();
      exits.get(canvas)?.delete(node.dataset.homeWidget!);
    }
    const dx = old ? old.left - next.left : 0,
      dy = old ? old.top - next.top : 0,
      sx = old && next.width ? old.width / next.width : 1,
      sy = old && next.height ? old.height / next.height : 1;
    if (!isNew && Math.abs(dx) + Math.abs(dy) < 0.1 && Math.abs(sx - 1) + Math.abs(sy - 1) < 0.001) {
      if (landing) delete node.dataset.homeMotion;
      continue;
    }
    const from: Keyframe = returnBox
      ? config.reduce
        ? { opacity: returnOpacity }
        : {
            opacity: returnOpacity,
            transform:
              'translate3d(' +
              (returnBox.left - next.left) +
              'px,' +
              (returnBox.top - next.top) +
              'px,0) scale(' +
              returnBox.width / next.width +
              ',' +
              returnBox.height / next.height +
              ')',
          }
      : config.reduce
        ? { opacity: isNew ? 0 : 0.65 }
        : isNew
          ? { opacity: 0, transform: 'scale(' + config.appear + ')' }
          : { transform: 'translate3d(' + dx + 'px,' + dy + 'px,0) scale(' + sx + ',' + sy + ')' };
    const to: Keyframe = config.reduce
      ? { opacity: 1 }
      : isNew
        ? { opacity: 1, transform: 'none' }
        : { transform: 'none' };
    const animation = node.animate([from, to], {
      duration: config.reduce ? config.reduced : landing || isNew ? config.drop : config.reflow,
      delay:
        config.reduce || landing || isNew || canvas.hasAttribute('data-home-drop-state')
          ? 0
          : Math.min(
              ordered.findIndex((value) => value.node === node),
              4,
            ) * config.stagger,
      easing: config.easing,
      fill: 'both',
    });
    flights.set(node, animation);
    animation.onfinish = () => {
      animation.cancel();
      if (flights.get(node) === animation) flights.delete(node);
      if (landing) delete node.dataset.homeMotion;
    };
  }
}
export function removeHomeVisual(canvas: HTMLElement | null, node: HTMLElement | null) {
  if (!canvas || !node) return;
  const config = homeMotionTokens(canvas),
    rect = node.getBoundingClientRect(),
    parent = canvas.getBoundingClientRect(),
    clone = node.cloneNode(true) as HTMLElement,
    id = node.dataset.homeWidget!,
    pending = exits.get(canvas) ?? new Map<string, { node: HTMLElement; animation: Animation }>();
  pending.get(id)?.animation.cancel();
  pending.get(id)?.node.remove();
  clone.removeAttribute('data-home-widget');
  clone.removeAttribute('data-home-grabbed');
  clone.dataset.homeExit = id;
  clone.setAttribute('aria-hidden', 'true');
  clone.inert = true;
  Object.assign(clone.style, {
    position: 'absolute',
    left: rect.left - parent.left + 'px',
    top: rect.top - parent.top + 'px',
    width: rect.width + 'px',
    height: rect.height + 'px',
    margin: '0',
    transform: 'none',
    pointerEvents: 'none',
  });
  canvas.appendChild(clone);
  const animation = clone.animate(
    config.reduce
      ? [{ opacity: 1 }, { opacity: 0 }]
      : [
          { opacity: 1, transform: 'scale(1)' },
          { opacity: 0, transform: 'scale(' + config.appear + ')' },
        ],
    { duration: config.reduce ? config.reduced : config.drop, easing: config.easing, fill: 'forwards' },
  );
  pending.set(id, { node: clone, animation });
  exits.set(canvas, pending);
  animation.onfinish = () => {
    clone.remove();
    if (pending.get(id)?.node === clone) pending.delete(id);
  };
}
function scrollParent(element: HTMLElement): HTMLElement {
  let candidate = element.parentElement;
  while (candidate) {
    const style = getComputedStyle(candidate);
    if (/auto|scroll/u.test(style.overflowY) && candidate.scrollHeight > candidate.clientHeight)
      return candidate;
    candidate = candidate.parentElement;
  }
  return (document.scrollingElement ?? document.documentElement) as HTMLElement;
}
export interface HomePointerStart {
  id: string;
  kind: 'move' | 'resize' | 'add';
  pointerId: number;
  startX: number;
  startY: number;
  origin: HomePlacement;
  allowed: readonly HomeSize[];
}
export interface HomePointerResult {
  target: HomeCell & HomeSize;
  keep: boolean;
  from: HomeBox;
}
export interface HomePointerController {
  dispose: () => void;
  cancel: () => void;
  refresh: () => void;
}
export function startHomePointer({
  canvas,
  node,
  placeholder,
  resizeFrame,
  sizeLabel,
  layout,
  start,
  hoverDelayMs,
  enabled,
  onPreview,
  onFinish,
}: {
  canvas: HTMLElement;
  node: HTMLElement;
  placeholder: HTMLElement | null;
  resizeFrame: HTMLElement | null;
  sizeLabel: HTMLElement | null;
  layout: readonly HomePlacement[];
  start: HomePointerStart;
  hoverDelayMs: 0 | 400 | 800 | 1500;
  enabled: () => boolean;
  onPreview: (target: (HomeCell & HomeSize) | null) => { layout: HomePlacement[]; valid: boolean };
  onFinish: (result: HomePointerResult) => void;
}): HomePointerController {
  const frameNode = motionFrame(node),
    originalTransform = frameNode.style.transform,
    visualStart = node.getBoundingClientRect();
  flights.get(node)?.cancel();
  flights.delete(node);
  const config = homeMotionTokens(canvas, hoverDelayMs),
    initialCanvas = canvas.getBoundingClientRect(),
    original = frameNode.getBoundingClientRect(),
    style = getComputedStyle(canvas),
    gap = number(style, '--home-grid-gap'),
    rowHeight = number(style, '--home-grid-row'),
    columnStep = (initialCanvas.width + gap) / HOME_COLUMNS,
    rowStep = rowHeight + gap,
    liftOffsetX = visualStart.left - original.left,
    liftOffsetY = visualStart.top - original.top;
  const elements = widgets(canvas),
    nodes = new Map(elements.map((element) => [element.dataset.homeWidget!, element])),
    neighbours = layout.flatMap((item) => {
      const element = nodes.get(item.id);
      if (!element || element === node) return [];
      const frame = motionFrame(element),
        pending = flights.get(element),
        visual = pending ? element.getBoundingClientRect() : null;
      const offsetX = visual ? visual.left - initialCanvas.left - item.column * columnStep : 0,
        offsetY = visual ? visual.top - initialCanvas.top - item.row * rowStep : 0;
      return [
        {
          element,
          frame,
          item,
          pending,
          marker: frame.getAttribute('data-home-neighbor-motion'),
          fromX: offsetX,
          fromY: offsetY,
          toX: offsetX,
          toY: offsetY,
          animation: null as Animation | null,
        },
      ];
    }),
    initialHeight = canvas.style.height,
    placeholderStyle = placeholder?.getAttribute('style') ?? null;
  const scroller = scrollParent(canvas),
    scrollStart = scroller.scrollTop,
    scrollRect =
      scroller === document.scrollingElement
        ? { top: 0, bottom: innerHeight }
        : scroller.getBoundingClientRect();
  let scrollPosition = scrollStart;
  const scrollTarget = scroller === document.scrollingElement ? window : scroller;
  function observeScroll() {
    scrollPosition = scroller.scrollTop;
  }
  const viewportTop = Math.max(0, scrollRect.top),
    viewportBottom = Math.min(innerHeight, scrollRect.bottom),
    header = node.querySelector('header')?.getBoundingClientRect();
  const offsetX = start.kind === 'add' ? original.width / 2 : start.startX - original.left,
    offsetY = start.kind === 'add' ? (header?.height ?? original.height) / 2 : start.startY - original.top;
  let canvasHeight = initialCanvas.height,
    needsMeasure = false;
  let x = start.startX,
    y = start.startY,
    anchorX = x,
    anchorY = y,
    stableAt = performance.now(),
    lastFrame = stableAt,
    frame = 0,
    stopped = false,
    moved = false,
    accepted = '',
    candidate = '',
    valid = true,
    transform = '',
    lastLabel = '',
    target: HomeCell & HomeSize = {
      column: start.origin.column,
      row: start.origin.row,
      columns: start.origin.columns,
      rows: start.origin.rows,
    };
  // Geometry and motion tokens are read once. During movement only cached coordinates and
  // composited transforms change; rich widget trees and their layout remain untouched.
  const translate = (x: number, y: number) => 'translate3d(' + x + 'px,' + y + 'px,0)';
  function retarget(neighbour: (typeof neighbours)[number], x: number, y: number) {
    if (neighbour.toX === x && neighbour.toY === y) return;
    const progress = neighbour.animation?.effect?.getComputedTiming().progress ?? 0,
      fromX = neighbour.animation
        ? neighbour.fromX + (neighbour.toX - neighbour.fromX) * progress
        : neighbour.toX,
      fromY = neighbour.animation
        ? neighbour.fromY + (neighbour.toY - neighbour.fromY) * progress
        : neighbour.toY;
    neighbour.animation?.cancel();
    neighbour.fromX = config.reduce ? x : fromX;
    neighbour.fromY = config.reduce ? y : fromY;
    neighbour.toX = x;
    neighbour.toY = y;
    // Effect progress already includes easing. Retarget from that cached point without layout
    // reads or inline style mutations, keeping the finished effect until the next target/release.
    neighbour.animation = neighbour.frame.animate(
      config.reduce
        ? [
            { transform: translate(x, y), opacity: 0.65 },
            { transform: translate(x, y), opacity: 1 },
          ]
        : [{ transform: translate(fromX, fromY) }, { transform: translate(x, y) }],
      {
        duration: config.reduce ? config.reduced : config.reflow,
        easing: config.easing,
        fill: 'both',
      },
    );
  }
  for (const neighbour of neighbours) {
    neighbour.pending?.cancel();
    flights.delete(neighbour.element);
    if (neighbour.element.dataset.homeMotion === 'settling') delete neighbour.element.dataset.homeMotion;
    neighbour.frame.dataset.homeNeighborMotion = '';
    retarget(neighbour, 0, 0);
  }
  function coordinates(element: HTMLElement, item: HomePlacement) {
    const values = {
      homeColumn: String(item.column),
      homeRow: String(item.row),
      homeColumns: String(item.columns),
      homeRows: String(item.rows),
    };
    for (const [key, value] of Object.entries(values))
      if (element.dataset[key] !== value) element.dataset[key] = value;
  }
  function paintPreview(preview: readonly HomePlacement[]) {
    const positions = new Map(preview.map((item) => [item.id, item]));
    for (const neighbour of neighbours) {
      const next = positions.get(neighbour.item.id);
      if (!next) continue;
      retarget(
        neighbour,
        (next.column - neighbour.item.column) * columnStep,
        (next.row - neighbour.item.row) * rowStep,
      );
      coordinates(neighbour.element, next);
    }
    const active = positions.get(start.id);
    if (active) {
      coordinates(node, active);
      if (placeholder) {
        placeholder.style.left = active.column * columnStep + 'px';
        placeholder.style.top = active.row * rowStep + 'px';
        placeholder.style.width = active.columns * columnStep - gap + 'px';
        placeholder.style.height = active.rows * rowStep - gap + 'px';
      }
    }
    const height = (Math.max(1, ...preview.map((item) => item.row + item.rows)) + 2) * rowStep - gap;
    if (canvasHeight !== height) {
      canvasHeight = height;
      canvas.style.height = height + 'px';
    }
  }
  node.dataset.homeGhost = '';
  node.dataset.homeMotion = start.kind === 'resize' ? 'resize' : 'drag';
  canvas.dataset.homeDwell = 'waiting';
  canvas.dataset.homeAutoscroll = 'none';
  const lifted = config.reduce
    ? null
    : node.animate([{ scale: '1' }, { scale: String(config.scale) }], {
        duration: config.mode,
        easing: config.easing,
      });
  const oldTransformOrigin = node.style.transformOrigin;
  const oldCursor = document.body.style.cursor,
    gestureCursor = start.kind === 'resize' ? 'nwse-resize' : 'grabbing';
  document.body.style.cursor = gestureCursor;
  node.style.transformOrigin = 'center';
  node.style.pointerEvents = 'none';
  function cleanup() {
    stopped = true;
    cancelAnimationFrame(frame);
    lifted?.cancel();
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', abort);
    window.removeEventListener('keydown', escape);
    window.removeEventListener('blur', abort);
    scrollTarget.removeEventListener('scroll', observeScroll);
    for (const neighbour of neighbours) {
      neighbour.animation?.cancel();
      if (neighbour.marker === null) delete neighbour.frame.dataset.homeNeighborMotion;
      else neighbour.frame.setAttribute('data-home-neighbor-motion', neighbour.marker);
      coordinates(neighbour.element, neighbour.item);
    }
    coordinates(node, start.origin);
    canvas.style.height = initialHeight;
    if (placeholder) {
      if (placeholderStyle === null) placeholder.removeAttribute('style');
      else placeholder.setAttribute('style', placeholderStyle);
    }
    frameNode.style.transform = originalTransform;
    node.style.pointerEvents = '';
    node.style.transformOrigin = oldTransformOrigin;
    if (document.body.style.cursor === gestureCursor) document.body.style.cursor = oldCursor;
    delete node.dataset.homeGhost;
    delete node.dataset.homeDropState;
    delete canvas.dataset.homeDropState;
    delete canvas.dataset.homeDwell;
    delete canvas.dataset.homeAutoscroll;
  }
  function finish(keep: boolean) {
    if (stopped) return;
    const actual = box((start.kind === 'resize' && resizeFrame ? resizeFrame : node).getBoundingClientRect());
    onFinish({ target, keep: keep && moved && valid && enabled(), from: actual });
    cleanup();
    node.dataset.homeMotion = 'settling';
  }
  function draw(now: number) {
    if (stopped) return;
    if (!enabled()) {
      finish(false);
      return;
    }
    if (needsMeasure) {
      canvasHeight = canvas.getBoundingClientRect().height;
      needsMeasure = false;
    }
    const elapsed = Math.max(0, now - lastFrame) / 1000;
    lastFrame = now;
    let velocity = 0;
    if (moved && x >= initialCanvas.left && x <= initialCanvas.right && config.edge > 0) {
      if (y < viewportTop + config.edge)
        velocity = -config.speed * Math.min(1, (viewportTop + config.edge - y) / config.edge);
      else if (y > viewportBottom - config.edge)
        velocity = config.speed * Math.min(1, (y - viewportBottom + config.edge) / config.edge);
    }
    const priorScroll = scrollPosition;
    let currentScroll = priorScroll;
    if (velocity) {
      scroller.scrollTop =
        priorScroll + Math.sign(velocity) * Math.min(Math.abs(velocity) * elapsed, config.edge);
      currentScroll = scroller.scrollTop;
      scrollPosition = currentScroll;
    }
    const scrollDelta = currentScroll - scrollStart,
      top = initialCanvas.top - scrollDelta;
    if (currentScroll !== priorScroll) {
      stableAt = now;
    }
    const auto = currentScroll === priorScroll ? 'none' : velocity > 0 ? 'down' : 'up';
    if (canvas.dataset.homeAutoscroll !== auto) canvas.dataset.homeAutoscroll = auto;
    let dx = start.kind === 'add' ? x - original.left - offsetX : x - start.startX + liftOffsetX,
      dy =
        start.kind === 'add'
          ? y - original.top - offsetY + scrollDelta
          : y - start.startY + scrollDelta + liftOffsetY;
    if (start.kind === 'resize') {
      const width = Math.max(1, Math.min(HOME_COLUMNS * columnStep - gap, original.width + dx)),
        height = Math.max(1, Math.min(4 * rowStep - gap, original.height + dy));
      if (resizeFrame) {
        const nextWidth = width + 'px',
          nextHeight = height + 'px';
        if (resizeFrame.style.width !== nextWidth) resizeFrame.style.width = nextWidth;
        if (resizeFrame.style.height !== nextHeight) resizeFrame.style.height = nextHeight;
      }
      const nearest = closestHomeSize((width + gap) / columnStep, (height + gap) / rowStep, start.allowed);
      target = { column: start.origin.column, row: start.origin.row, ...nearest };
      if (placeholder) {
        const nextWidth = nearest.columns * columnStep - gap + 'px',
          nextHeight = nearest.rows * rowStep - gap + 'px';
        if (placeholder.style.width !== nextWidth) placeholder.style.width = nextWidth;
        if (placeholder.style.height !== nextHeight) placeholder.style.height = nextHeight;
      }
      dx = 0;
      dy = 0;
    } else
      target = {
        column: Math.max(
          0,
          Math.min(
            HOME_COLUMNS - start.origin.columns,
            Math.round((original.left - initialCanvas.left + dx) / columnStep),
          ),
        ),
        row: Math.max(0, Math.round((original.top - initialCanvas.top + dy) / rowStep)),
        columns: start.origin.columns,
        rows: start.origin.rows,
      };
    if (!config.reduce) {
      const next = 'translate3d(' + dx + 'px,' + dy + 'px,0)';
      if (next !== transform) {
        frameNode.style.transform = next;
        transform = next;
      }
    }
    const label = target.columns + ' × ' + target.rows;
    if (sizeLabel && label !== lastLabel) {
      sizeLabel.textContent = label;
      lastLabel = label;
    }
    const distance = Math.hypot(x - anchorX, y - anchorY);
    if (distance > config.slop) {
      anchorX = x;
      anchorY = y;
      stableAt = now;
    }
    const within =
      x >= initialCanvas.left &&
      x <= initialCanvas.right &&
      y >= Math.max(top, viewportTop) &&
      y <= Math.min(top + canvasHeight, viewportBottom) &&
      target.row + target.rows <= HOME_MAX_ROWS;
    const origin =
      target.column === start.origin.column &&
      target.row === start.origin.row &&
      target.columns === start.origin.columns &&
      target.rows === start.origin.rows;
    const signature = within
      ? target.column + ':' + target.row + ':' + target.columns + ':' + target.rows
      : 'outside';
    if (candidate !== signature) {
      candidate = signature;
      valid = within;
      if (config.hover && !origin && canvas.dataset.homeDwell !== 'waiting')
        canvas.dataset.homeDwell = 'waiting';
    }
    // Resolve once per logical cell, always against the gesture's immutable start layout.
    // Leaving the board or returning home restores neighbours immediately in every delay mode.
    if (moved && !within && accepted !== signature) {
      accepted = signature;
      const preview = onPreview(null);
      paintPreview(preview.layout);
    } else if (moved && within && (origin || now - stableAt >= config.hover) && accepted !== signature) {
      accepted = signature;
      const preview = onPreview({ ...target });
      valid = preview.valid;
      paintPreview(preview.layout);
    }
    const dwell = within && accepted === signature ? 'accepted' : 'waiting';
    if (canvas.dataset.homeDwell !== dwell) canvas.dataset.homeDwell = dwell;
    const state = valid ? 'valid' : 'invalid';
    if (node.dataset.homeDropState !== state) node.dataset.homeDropState = state;
    if (canvas.dataset.homeDropState !== state) canvas.dataset.homeDropState = state;
    frame = requestAnimationFrame(draw);
  }
  function move(event: PointerEvent) {
    if (event.pointerId !== start.pointerId) return;
    x = event.clientX;
    y = event.clientY;
    moved = moved || Math.hypot(x - start.startX, y - start.startY) > config.slop;
    event.preventDefault();
  }
  function up(event: PointerEvent) {
    if (event.pointerId !== start.pointerId) return;
    x = event.clientX;
    y = event.clientY;
    scrollPosition = scroller.scrollTop;
    cancelAnimationFrame(frame);
    draw(performance.now());
    if (stopped) return;
    const hit = document.elementFromPoint(x, y);
    const inside = !!hit && canvas.contains(hit);
    finish(inside);
  }
  function abort(event: Event) {
    if (event instanceof PointerEvent && event.pointerId !== start.pointerId) return;
    finish(false);
  }
  function escape(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    finish(false);
  }
  window.addEventListener('pointermove', move, { passive: false });
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', abort);
  window.addEventListener('keydown', escape);
  window.addEventListener('blur', abort);
  scrollTarget.addEventListener('scroll', observeScroll, { passive: true });
  draw(performance.now());
  return {
    dispose: () => {
      if (!stopped) cleanup();
    },
    cancel: () => finish(false),
    refresh: () => {
      needsMeasure = true;
    },
  };
}
